import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"
import { useRecoilValue } from "recoil"

import { isPayingUserSelector, userAtom } from "@/atoms/userAtom"
import { useOrg } from "@/hooks/useOrg"
import { IPreference } from "@/types/organizations.types"
import { SecondaryText, SemiBoldLabelText } from "@/UI"
import { Input, Select, Switch } from "antd"
import { AnimatePresence, motion } from "framer-motion"
import KosmoButton from "@/UI/Buttons/KosmoButton"
import { useZones } from "@/hooks/useZones" // Add this import
import { useFlags } from "flagsmith/react"
import React from "react"

interface IAutoassignProps {}

const DEFAULT_REASSIGN_TIME_IN_SECONDS = 0

const Autoassign: React.FC<IAutoassignProps> = () => {
  const { t } = useTranslation()
  const user = useRecoilValue(userAtom)
  const isPayingUser = useRecoilValue(isPayingUserSelector)
  const { updateOrgPreferences, isLoading } = useOrg()
  const { zones } = useZones() // Add this line to get zones
  const [maxDriversPerOrder, setMaxDriversPerOrder] = useState<number>()
  const [maxOrdersPerDriver, setMaxOrdersPerDriver] = useState<number>()
  const [reassignTime, setReassignTime] = useState<number>()
  const [disabled, setDisabled] = useState<boolean>(false)
  const [selectedZones, setSelectedZones] = useState<string[]>([])
  const flags = useFlags(["nash_edg_demo"])

  const handleUpdate = async () => {
    await updateOrgPreferences({
      ...user.preference,
      autoassign: {
        maxDriversPerOrder,
        maxOrdersPerDriver,
        reassignTime: (reassignTime || DEFAULT_REASSIGN_TIME_IN_SECONDS) * 60,
        disabled,
        selectedZones, // Add this line
      },
    } as IPreference)
  }

  useEffect(() => {
    setMaxDriversPerOrder(user.preference?.autoassign?.maxDriversPerOrder)
    setMaxOrdersPerDriver(user.preference?.autoassign?.maxOrdersPerDriver)
    setReassignTime(
      user.preference?.autoassign?.reassignTime
        ? user.preference?.autoassign?.reassignTime / 60
        : DEFAULT_REASSIGN_TIME_IN_SECONDS / 60
    )
    setDisabled(user.preference?.autoassign?.disabled || false)
  }, [user.preference])

  const isFormDirty =
    maxDriversPerOrder !== user?.preference?.autoassign?.maxDriversPerOrder ||
    maxOrdersPerDriver !== user?.preference?.autoassign?.maxOrdersPerDriver ||
    (reassignTime || DEFAULT_REASSIGN_TIME_IN_SECONDS) * 60 !==
      user?.preference?.autoassign?.reassignTime ||
    disabled !== user?.preference?.autoassign?.disabled

  return (
    <>
      <div className="flex w-full">
        <div className="w-full">
          <AnimatePresence>
            <div className="flex flex-col space-y-3">
              <motion.div
                className="rounded-md bg-gray-50 px-4 overflow-hidden"
                initial={{
                  maxHeight: 0,
                  opacity: 0,
                  x: 30,
                  paddingTop: 0,
                  paddingBottom: 0,
                }}
                animate={{
                  maxHeight: "150px",
                  opacity: 1,
                  x: 0,
                  paddingTop: 12,
                  paddingBottom: 12,
                }}
                exit={{
                  maxHeight: 0,
                  opacity: 0,
                  x: 30,
                  paddingTop: 0,
                  paddingBottom: 0,
                }}
              >
                <SecondaryText>
                  {t("profile.DispatchSettings.autoassignSettingsOffDesc")}
                </SecondaryText>
              </motion.div>
              <div className="flex">
                <Switch
                  defaultChecked={!disabled}
                  checked={!disabled}
                  onChange={(checked) => setDisabled(!checked)}
                  disabled={!isPayingUser}
                />
              </div>
              {flags.nash_edg_demo.enabled && (
                <div className="flex flex-col">
                  <SemiBoldLabelText>Driver preference</SemiBoldLabelText>
                  <Select
                    className="!max-w-[200px]"
                    value={"earliestETA"}
                    // onChange={(value) => setAutoassignType(value)}
                    disabled={!isPayingUser}
                  >
                    <Select.Option value="earliestETA">Earliest ETA</Select.Option>
                    <Select.Option value="driverRanking">Driver Ranking</Select.Option>
                    <Select.Option value="cheapest">Delivery Cost</Select.Option>
                  </Select>
                </div>
              )}
              <div className="flex flex-col">
                <SemiBoldLabelText>
                  {t("profile.DispatchSettings.maxDriversPerOrder")}
                </SemiBoldLabelText>
                <div className="flex space-x-3">
                  <Input
                    prefix=" "
                    className="!max-w-[100px]"
                    placeholder=""
                    value={maxDriversPerOrder || 0 > 0 ? maxDriversPerOrder : undefined}
                    type="number"
                    status={maxDriversPerOrder === 0 && !disabled ? "error" : undefined}
                    onChange={(e) => setMaxDriversPerOrder(Number(e.target.value))}
                    style={{ borderRadius: "none !important" }}
                    disabled={!isPayingUser}
                  />
                </div>
              </div>
              <div className="flex flex-col">
                <SemiBoldLabelText>
                  {t("profile.DispatchSettings.maxOrdersPerDriver")}
                </SemiBoldLabelText>
                <div className="flex space-x-3">
                  <Input
                    prefix=" "
                    className="!max-w-[100px]"
                    placeholder={"Unlimited"}
                    value={maxOrdersPerDriver || 0 > 0 ? maxOrdersPerDriver : undefined}
                    type="number"
                    onChange={(e) => setMaxOrdersPerDriver(Number(e.target.value))}
                    style={{ borderRadius: "none !important" }}
                    disabled={!isPayingUser}
                  />
                </div>
              </div>
              <div className="flex flex-col">
                <SemiBoldLabelText>{t("profile.DispatchSettings.reassignTime")}</SemiBoldLabelText>
                <div className="flex space-x-3">
                  <Input
                    prefix=" "
                    className="withAddOnInput !max-w-[150px]"
                    placeholder={"15"}
                    value={reassignTime || 0 > 0 ? reassignTime : undefined}
                    type="number"
                    onChange={(e) => setReassignTime(Number(e.target.value))}
                    addonAfter="Minutes"
                    style={{ borderRadius: "none !important" }}
                    disabled={!isPayingUser}
                  />
                </div>
              </div>
              {flags.nash_edg_demo.enabled && (
                <div className="flex flex-col">
                  <SemiBoldLabelText>Zones</SemiBoldLabelText>
                  <Select
                    mode="multiple"
                    style={{ width: "100%" }}
                    placeholder="Select zones"
                    value={selectedZones}
                    onChange={(values) => setSelectedZones(values)}
                    disabled={!isPayingUser}
                  >
                    {zones.map((zone) => (
                      <Select.Option key={zone.id} value={zone.id}>
                        {zone.name}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              )}
              <div className="flex">
                {isFormDirty && (
                  <KosmoButton onClick={handleUpdate} isDisabled={isLoading || !isPayingUser}>
                    {t("common.save")}
                  </KosmoButton>
                )}
              </div>
            </div>
          </AnimatePresence>
        </div>
      </div>
    </>
  )
}

export default Autoassign
